import {
	ICampaign,
	ICampaigns,
	ICampaignsCalendar,
	ICreativeRegeneratePayload,
} from 'src/lib/schemas';
import http from './http';
import {
	ICreative,
	IDesignDirection,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { processCallback } from 'src/lib/utils/processCallback';

const apiVersion = 'v2';
const campaignUrl = `/${apiVersion}/campaign`;

function findElementByName(
	elements: any[] | undefined,
	targetName: string,
): any | undefined {
	if (!elements) return undefined;
	for (const el of elements) {
		if (el.name === targetName) {
			return el;
		}
		if (el.type === 'composition' && Array.isArray(el.elements)) {
			const found = findElementByName(el.elements, targetName);
			if (found) return found;
		}
	}
	return undefined;
}

function adaptCreativeToLegacy(creative: ICreative): ICreative {
	if (
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes)
	) {
		const clone = { ...creative, attributes: { ...creative.attributes } };
		const { elements } = clone.attributes;

		const ctaEl = findElementByName(elements, '$direction.callToAction');
		const headlineEl =
			findElementByName(elements, '$direction.headline') ||
			findElementByName(elements, '$title');

		(clone.attributes as any).headline = headlineEl?.text || '';
		(clone.attributes as any).callToAction = ctaEl?.text || '';

		return clone;
	}
	return creative;
}

function adaptDesignDirection(dd: IDesignDirection): IDesignDirection {
	if (dd.adEngine === 'creatomate' && isCreatomateAttributes(dd.attributes)) {
		const clone = { ...dd, attributes: { ...dd.attributes } };
		return clone;
	}
	return dd;
}

function adaptCampaignStruct(campaign: ICampaign): ICampaign {
	const adaptedCreatives = campaign.creatives?.map(adaptCreativeToLegacy) || [];
	const adaptedDD = campaign.designDirections?.map(adaptDesignDirection) || [];

	return {
		...campaign,
		creatives: adaptedCreatives,
		designDirections: adaptedDD,
	};
}

export const getCampaign = async (id: string) => {
	const startTime = performance.now();
	try {
		const { data } = await http.get<ICampaign>(`${campaignUrl}/${id}`);
		return data;
	} catch (error) {
		throw error;
	}
	// const adapted = adaptCampaignStruct(data);
	// return adapted;
};

export const createOrUpdateCampaign = async (
	payload: Partial<Omit<ICampaign, 'id'>>,
	id: string,
): Promise<ICampaign> => {
	const shouldIncludeTemplateId =
		sessionStorage.getItem('templateSelected') === 'true';

	const payloadToSend = shouldIncludeTemplateId
		? payload
		: { ...payload, templateId: undefined };

	if (id && id !== 'new') {
		const { data } = await http.patch<ICampaign>(
			`${campaignUrl}/${id}`,
			payloadToSend,
		);
		return data;
	}

	const { data } = await http.post<ICampaign>(campaignUrl, payloadToSend);
	return data;
};

export const launchCampaign = async (id: string) => {
	const response = await http.post(`${campaignUrl}/launch/${id}`);
	return response.data;
};
export const publishCampaignChannels = async (
	campaignId: string,
	placements: string[],
) => {
	const body = {
		inputs: {
			campaignId,
			placements,
		},
	};

	const { data } = await http.post(
		'/v2/apps/fusion_ai.publish_content/execute/publish_channels',
		body,
	);

	return data;
};

export const publishCampaignChannelsAndWait = async (
	campaignId: string,
	placements: string[],
): Promise<any> => {
	try {
		const publishResponse = await publishCampaignChannels(
			campaignId,
			placements,
		);
		const finalResponse = await processCallback(publishResponse.callback);
		return finalResponse;
	} catch (error) {
		return null;
	}
};

export const getCampaigns = async (query: string) => {
	const { data } = await http.get<ICampaigns>(`${campaignUrl}?${query}`);
	return data;
};

export const archiveCampaign = async (id: string) => {
	const { data } = await http.patch<ICampaign>(`${campaignUrl}/${id}`, {
		status: 'archived',
	});
	return data;
};

export const duplicateCampaign = async (id: string): Promise<ICampaign> => {
	const { data } = await http.post<ICampaign>(`${campaignUrl}/${id}/duplicate`);
	return data;
};

export const executeCampaignCreatives = async (
	campaignId: string,
	backgroundPrompt?: string,
) => {
	const payload: { backgroundPrompt?: string } = {};
	if (backgroundPrompt) {
		payload.backgroundPrompt = backgroundPrompt;
	}
	await http.post(`${campaignUrl}/execute/${campaignId}`, payload);
};

export const genrateCampaignCreatives = async (
	campaign: ICampaign,
	designDirectionId?: string,
	onlyUnprocessed: boolean = false,
) => {
	const id = designDirectionId ? `/${designDirectionId}` : '';
	await http.post(
		`${campaignUrl}/${campaign.id}/generate-creatives${id}?onlyUnprocessed=${onlyUnprocessed}`,
	);
};

export const genrateCampaignDesignDirections = async (
	campaign: ICampaign,
	templateId?: string,
) => {
	if (templateId) {
		await http.post(`${campaignUrl}/${campaign.id}/design-direction`, {
			templateId,
		});
	} else {
		await http.post(`${campaignUrl}/${campaign.id}/design-direction`);
	}
};

export const editCampaignDesignDirections = async (
	campaign: ICampaign,
	designId: string,
	payload: any,
) => {
	await http.patch(
		`${campaignUrl}/${campaign.id}/design-direction/${designId}`,
		payload,
	);
};

export const generateDesignDirectionWithTemplate = async (
	campaign: ICampaign,
	templateId: string,
) => {
	try {

		await http.post(`${campaignUrl}/${campaign.id}/add-design-direction/1`, {
			templateId,
		});
	} catch (error: any) {
		throw new Error(`Failed to generate design direction: ${error.message}`);
	}
};

export const generateDesignDirectionsWithTemplates = async (
	campaign: ICampaign,
	templateIds: string[],
) => {
	try {
		const numTemplates = templateIds.length;
		await http.post(
			`${campaignUrl}/${campaign.id}/add-design-direction/${numTemplates}`,
			{
				templateIds,
			},
		);
	} catch (error: any) {
		throw new Error(`Failed to generate design directions: ${error.message}`);
	}
};

export const generateDesignDirectionsFromLayers = async (
	campaign: ICampaign,
	layeredDesignDirections: any[],
) => {
	try {
		const numTemplates = layeredDesignDirections.length;
		const templateIds = layeredDesignDirections.map(
			(dd) => dd.outputs.templateId,
		);
		await http.post(
			`${campaignUrl}/${campaign.id}/add-design-direction/${numTemplates}`,
			{ templateIds, adEngine: 'creatomate' },
		);
	} catch (error: any) {
		throw new Error(`Failed to generate design directions: ${error.message}`);
	}
};

export const genrateNewDesignDirection = async (
	campaign: ICampaign,
	payload?: any,
) => {
	await http.post(
		`${campaignUrl}/${campaign.id}/add-design-direction/1`,
		payload,
	);
};

export const removeDesignDirection = async (
	campaign: ICampaign,
	designDirectionId: string,
) => {
	await http.delete(
		`${campaignUrl}/${campaign.id}/design-direction/${designDirectionId}`,
	);
};

export const genrateCampaignDesignDirectionsWithTemplate = async (
	campaignId: string,
	templateId?: string,
) => {
	if (templateId) {
		await http.post(`${campaignUrl}/${campaignId}/design-direction`, {
			templateId,
		});
	} else {
		await http.post(`${campaignUrl}/${campaignId}/design-direction`);
	}
};

export const regenerateCampaignCreative = async (
	campaignId: string,
	payload: ICreativeRegeneratePayload,
) => {
	const { data } = await http.patch<ICampaign>(
		`${campaignUrl}/regenerate/${campaignId}/${payload.creativeId}`,
		payload,
	);
	return data;
};

export const regenerateCreativesVariant = async (
	id: string,
	variant: 'A' | 'B',
	payload: Partial<ICreativeRegeneratePayload>,
) => {
	const { data } = await http.patch<ICampaign>(
		`${campaignUrl}/regenerate/${id}/${variant}`,
		payload,
	);
	return data;
};

export const regenerateCampaignDesignDirection = async (
	campaignId: string,
	designDirectionId: string,
	payload: any,
): Promise<ICampaign> => {
	const { data } = await http.patch<ICampaign>(
		`${campaignUrl}/regenerate/${campaignId}/design-direction/${designDirectionId}`,
		payload,
	);
	return data;
};

export const getCampaignCalendar = async (query?: Record<string, string>) => {
	const { data } = await http.get<ICampaignsCalendar>(
		`${campaignUrl}/calendar?${new URLSearchParams(query).toString()}`,
	);
	return data;
};

export const updateCampaignCreative = async (
	id: string,
	creativeId: string,
	payload: any,
) => {
	const { data } = await http.patch<ICreative>(
		`${campaignUrl}/${id}/update-creative/${creativeId}`,
		payload,
	);
	return data;
};

export const getInterpretations = async (id: string) => {
	const { data } = await http.post(`${campaignUrl}/${id}/interpretations`);
	return data;
};

export const deactivateCampaign = async (id: string): Promise<ICampaign> => {
	const { data } = await http.post<ICampaign>(`${campaignUrl}/${id}/deactivate`);
	return data;
};

// AI Prefill types
export interface IAIPrefillResponse {
	campaignName: string;
	campaignDescription: string;
	suggestedServiceAreas: string[];
	suggestedCustomerProfiles: Array<{
		name: string;
		description: string;
	}>;
	suggestedChannels: string[];
}

export const generateAIPrefill = async (catalogId: string): Promise<IAIPrefillResponse> => {
	const { data } = await http.post<IAIPrefillResponse>(`${campaignUrl}/ai-prefill`, {
		catalogId,
	});
	return data;
};

export const generateAIPrefillFromUrl = async (url: string, title: string, description?: string): Promise<IAIPrefillResponse> => {
	const { data } = await http.post<IAIPrefillResponse>(`${campaignUrl}/ai-prefill-url`, {
		url,
		title,
		description,
	});
	return data;
};

export interface IGenerateCustomerProfilesResponse {
	success: boolean;
	profiles: Array<{
		_id: string;
		name: string;
		description: string;
		customer_rules: Array<{
			conditionId: string;
			description: string;
			file_id: string;
			facebookId: string | null;
			googleId: string | null;
		}>;
		customer_tones: string[];
		customer_keywords: string[];
	}>;
	profileIds: string[];
}

/**
 * Generate customer profiles for a campaign using AI
 * This calls the backend which uses generateCustomerProfileForCampaign
 * and waits for the AI to complete before returning
 */
export const generateCampaignCustomerProfiles = async (
	campaignId: string,
	businessDescription: string,
): Promise<IGenerateCustomerProfilesResponse> => {
	const { data } = await http.post<IGenerateCustomerProfilesResponse>(
		`${campaignUrl}/${campaignId}/customer-profiles`,
		{ businessDescription },
	);
	return data;
};